import { useTranslation } from 'react-i18next';
import InputField from '../ui/InputField';
import {
    type Coordinates,
    coordinatesAfterInput,
    coordinatesPreviewSrc,
    coordinatesState,
    mapsSearchUrl,
} from '../../utils/coordinates';

interface CoordinatesFieldsProps {
    value: Coordinates;
    onChange: (next: Coordinates) => void;
    /** Street, house number, postal code, city — for the "open in Google Maps" link. */
    addressParts: Array<string | null | undefined>;
    /** The address is entered in the same form, above the fields. */
    addressAbove?: boolean;
}

/**
 * The map pin of a location — one building block for every form that enters
 * one: room creation at a new address, the house and station wizard, and the
 * location edit on the house or station page (PO, 2026-10-16).
 *
 * Optional, with the consequence said out loud, and with a preview of exactly
 * the map the customer sees: the operator checks the pin by looking at it,
 * which catches the usual mistake (latitude and longitude swapped) without a
 * rule that would have to guess where the tenant is.
 */
const CoordinatesFields = ({ value, onChange, addressParts, addressAbove = true }: CoordinatesFieldsProps) => {
    const { t } = useTranslation();
    const state = coordinatesState(value);
    const previewSrc = coordinatesPreviewSrc(value);

    return (
        <div className={'flex flex-col gap-3'}>
            <div className={'grid grid-cols-2 gap-3 sm:max-w-md'}>
                <InputField
                    label={t('asset_detail.fields.latitude')}
                    value={value.geoLat != null ? value.geoLat.toString() : ''}
                    onChange={(val) => onChange(coordinatesAfterInput(value, 'geoLat', val))}
                    error={state === 'incomplete' && value.geoLat == null}
                />
                <InputField
                    label={t('asset_detail.fields.longitude')}
                    value={value.geoLng != null ? value.geoLng.toString() : ''}
                    onChange={(val) => onChange(coordinatesAfterInput(value, 'geoLng', val))}
                    error={state === 'incomplete' && value.geoLng == null}
                />
            </div>

            {state === 'incomplete' && (
                <p className={'text-sm text-error'} role={'alert'}>{t('coordinates.incomplete')}</p>
            )}
            {state === 'missing' && (
                <p className={'text-sm text-foreground/60'}>{t('coordinates.missing_hint')}</p>
            )}

            {previewSrc && (
                <div className={'flex flex-col gap-1'}>
                    <p className={'text-sm text-foreground/70'}>{t('coordinates.preview_label')}</p>
                    <div className={'w-full h-48 rounded-[5px] overflow-hidden border border-foreground/10'}>
                        <iframe
                            title={t('coordinates.preview_label')}
                            src={previewSrc}
                            className={'w-full h-full border-0'}
                            loading={'lazy'}
                        />
                    </div>
                    <p className={'text-xs text-foreground/60'}>{t('coordinates.check_hint')}</p>
                </div>
            )}

            <div className={'text-xs text-foreground/70 bg-foreground/5 border border-foreground/10 rounded-[5px] px-4 py-3'}>
                <p className={'font-medium mb-1'}>{t('asset_detail.fields.coordinates_hint_title')}</p>
                <ol className={'list-decimal pl-5 space-y-0.5 text-foreground/60'}>
                    {addressAbove && <li>{t('asset_detail.fields.coordinates_hint_step_1')}</li>}
                    <li>
                        <a
                            href={mapsSearchUrl(addressParts)}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            className={'text-primary underline hover:text-primary/80'}
                        >
                            {t('asset_detail.fields.coordinates_hint_step_2_link')}
                        </a>
                        {t('asset_detail.fields.coordinates_hint_step_2_text')}
                    </li>
                    <li>{t('asset_detail.fields.coordinates_hint_step_3')}</li>
                    <li>{t('asset_detail.fields.coordinates_hint_step_4')}</li>
                </ol>
            </div>
        </div>
    );
};

export default CoordinatesFields;
